import { Sparkles, FileUp, Mail, MessageSquare, PhoneCall, Video, Code2, Check, Loader2, FileText, Trophy, Send } from "lucide-react";
import { Window, ScorecardMock } from "@/components/public/home/mocks";

const Chip = ({ children, tone = "slate" }) => {
  const t = { slate: "bg-slate-100 text-slate-700 border-slate-200", indigo: "bg-indigo-50 text-indigo-700 border-indigo-200", emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", amber: "bg-amber-50 text-amber-700 border-amber-200", cyan: "bg-cyan-50 text-cyan-700 border-cyan-200" }[tone];
  return <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border font-medium ${t}`}>{children}</span>;
};
const Row = ({ label, children, i = 0 }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0 rs-fade-up" style={{ animationDelay: `${i * 110}ms` }}>
    <span className="w-24 shrink-0 text-xs text-slate-500">{label}</span><div className="flex-1 min-w-0 flex flex-wrap gap-1.5 text-sm text-slate-900">{children}</div>
  </div>
);

export function JobPreview() {
  return (
    <Window dark={false} title="recruitsift.com · New job">
      <div className="p-5">
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-indigo-300 bg-indigo-50/50 px-4 py-3">
          <FileUp className="w-5 h-5 text-indigo-600" />
          <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-900 truncate">senior-backend-engineer-JD.pdf</div><div className="text-[11px] text-slate-500">Uploaded · parsed by AI</div></div>
          <Chip tone="indigo"><Sparkles className="w-3 h-3" /> Auto-filled</Chip>
        </div>
        <div className="mt-4">
          <Row i={1} label="Role">Senior Backend Engineer</Row>
          <Row i={2} label="Client">Nexus Systems · Payments account</Row>
          <Row i={3} label="Must-haves">{["Python", "PostgreSQL", "AWS", "Microservices"].map((s) => <Chip key={s}>{s}</Chip>)}</Row>
          <Row i={4} label="Knockouts"><span className="text-slate-700">Work authorisation · Notice ≤ 60 days</span></Row>
          <Row i={5} label="Rounds"><Chip tone="emerald"><PhoneCall className="w-3 h-3" /> Voice</Chip><Chip tone="cyan"><Video className="w-3 h-3" /> Video</Chip><Chip tone="indigo"><Code2 className="w-3 h-3" /> Coding</Chip></Row>
        </div>
      </div>
    </Window>
  );
}

export function InvitePreview() {
  const rows = [["Priya Raman", "Opened", "emerald"], ["Marcus Vale", "Sent", "indigo"], ["Lena Kowalski", "Sent", "indigo"], ["Omar Siddiq", "Reminder in 24h", "amber"]];
  return (
    <Window dark={false} title="recruitsift.com · Invite candidates">
      <div className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Chip><FileText className="w-3 h-3" /> candidates.csv · 42 rows imported</Chip>
          <div className="flex gap-1.5"><Chip tone="indigo"><Mail className="w-3 h-3" /> Email</Chip><Chip tone="cyan"><MessageSquare className="w-3 h-3" /> SMS</Chip></div>
        </div>
        <div className="mt-4 divide-y divide-slate-100">
          {rows.map(([n, st, t], i) => (
            <div key={n} className="flex items-center gap-3 py-2.5 rs-fade-up" style={{ animationDelay: `${i * 110}ms` }}>
              <div className="w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center text-[11px] font-semibold">{n.split(" ").map((x) => x[0]).join("")}</div>
              <div className="flex-1 min-w-0 text-sm font-medium text-slate-900 truncate">{n}</div>
              <Chip tone={t}>{st}</Chip>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500"><Send className="w-3.5 h-3.5 text-indigo-500" /> 42 invites sent · links valid 7 days</div>
      </div>
    </Window>
  );
}

export function RoundsPreview() {
  const rounds = [
    { Icon: PhoneCall, n: "AI voice interview", s: "Completed · 14 min", score: 88, tone: "emerald" },
    { Icon: Video, n: "AI video interview", s: "In progress · Q4 of 6", live: true, tone: "cyan" },
    { Icon: Code2, n: "Coding round", s: "Unlocks after video", tone: "slate" },
  ];
  return (
    <Window dark={false} title="recruitsift.com · Priya Raman · live">
      <div className="p-5 space-y-3">
        {rounds.map(({ Icon, n, s, score, live, tone }, i) => (
          <div key={n} className={`flex items-center gap-3 rounded-xl border p-3 rs-fade-up ${live ? "border-cyan-200 bg-cyan-50/40" : "border-slate-200"}`} style={{ animationDelay: `${i * 120}ms` }}>
            <div className={`w-9 h-9 rounded-lg grid place-items-center ${tone === "emerald" ? "bg-emerald-50 text-emerald-600" : tone === "cyan" ? "bg-cyan-50 text-cyan-600" : "bg-slate-100 text-slate-400"}`}><Icon className="w-4 h-4" /></div>
            <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-900">{n}</div><div className="text-[11px] text-slate-500">{s}</div></div>
            {score && <span className="font-mono text-sm font-semibold text-emerald-600 flex items-center gap-1"><Check className="w-4 h-4" />{score}</span>}
            {live && <div className="flex items-end gap-0.5 h-5">{[0, 1, 2, 3, 4].map((k) => <span key={k} className="w-1 bg-cyan-500 rounded-full rs-wave" style={{ animationDelay: `${k * 0.12}s` }} />)}</div>}
            {!score && !live && <Loader2 className="w-4 h-4 text-slate-300" />}
          </div>
        ))}
        <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600 leading-relaxed"><b className="text-indigo-600">AI:</b> "How would you design idempotent payment retries?" <span className="block mt-1"><b className="text-slate-900">Priya:</b> "I'd attach an idempotency key per request and store the outcome…"</span></div>
      </div>
    </Window>
  );
}

export const ScorePreview = () => <ScorecardMock dark={false} />;

export function ShortlistPreview() {
  const rows = [["Alex Rivera", 91, "Strong hire", "emerald"], ["Priya Raman", 86, "Hire", "indigo"], ["Lena Kowalski", 74, "Maybe", "amber"]];
  return (
    <Window dark={false} title="recruitsift.com · Shortlist · Nexus Systems">
      <div className="p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Trophy className="w-4 h-4 text-amber-500" /> Ranked shortlist · Senior Backend Engineer</div>
        <div className="mt-4 space-y-2">
          {rows.map(([n, s, r, t], i) => (
            <div key={n} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 rs-fade-up" style={{ animationDelay: `${i * 120}ms` }}>
              <span className="w-6 text-center font-mono text-xs text-slate-400">#{i + 1}</span>
              <div className="flex-1 min-w-0 text-sm font-medium text-slate-900 truncate">{n}</div>
              <Chip tone={t}>{r}</Chip>
              <span className="w-8 text-right font-mono font-semibold text-slate-900">{s}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Chip tone="indigo"><FileText className="w-3 h-3" /> 3 PDF reports shared with hiring manager</Chip>
          <Chip tone="emerald"><Check className="w-3 h-3" /> Alex Rivera marked Hired</Chip>
        </div>
      </div>
    </Window>
  );
}
